import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import ImprovementFeed from '../components/ImprovementFeed';
import {
  Lightning, Plus, Play, Pause, CheckCircle, XCircle, Clock,
  Pulse, EnvelopeSimple, WebhooksLogo, Timer, HandTap, CaretRight,
  ArrowsClockwise, Trash, ArrowLeft,
  UsersThree, ChatText, Microphone, FileText, Globe,
  ArrowRight, NotePencil, TextAlignLeft,
} from '@phosphor-icons/react';
import TriggerSelector from '../components/TriggerSelector';

/* ── Trigger icons ── */
const TRIGGER_ICON = {
  email: EnvelopeSimple,
  webhook: WebhooksLogo,
  schedule: Timer,
  manual: HandTap,
};

const TRIGGER_LABEL = {
  email: 'Runs on new email',
  webhook: 'Runs on event',
  schedule: 'Runs on schedule',
  manual: 'Runs manually',
};

function timeAgo(iso) {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/* ── System Card ── */
function SystemCard({ system, onToggle, onDelete, onClick }) {
  const isActive = system.status === 'active';
  const TriggerIcon = TRIGGER_ICON[system.trigger_type] || HandTap;
  const latestRun = system.latest_run;

  return (
    <div
      onClick={onClick}
      data-testid={`system-card-${system.system_id}`}
      className={`bg-[#1E2836] border rounded-2xl p-6 cursor-pointer transition-all duration-200 group shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)] hover:-translate-y-[3px] hover:shadow-[0_12px_28px_rgba(0,0,0,0.45)] ${
        isActive ? 'border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.1)]' : 'border-[rgba(255,255,255,0.08)] opacity-60 hover:opacity-80'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-semibold text-[#F0F4F8] truncate" data-testid="system-name">
            {system.name}
          </h3>
          <p className="text-[12px] text-[#7D8896] mt-1 line-clamp-1">{system.description}</p>
        </div>
        <div className={`w-[6px] h-[6px] rounded-full mt-1.5 flex-shrink-0 ${isActive ? 'bg-[#4ADE80]' : 'bg-[rgba(255,255,255,0.3)]'}`} />
      </div>

      {/* Trigger */}
      <div className="flex items-center gap-2 text-[11px] text-[#5C6778] mb-4">
        <TriggerIcon size={12} />
        <span>{TRIGGER_LABEL[system.trigger_type] || 'Manual trigger'}</span>
      </div>

      {/* Workers / Steps preview */}
      <div className="flex items-center gap-1.5 mb-4">
        {(system.worker_ids || []).length > 0 ? (
          <>
            <UsersThree size={12} className="text-[#00E5C060]" />
            <span className="text-[10px] text-[#3A4150]">{system.worker_ids.length} worker{system.worker_ids.length !== 1 ? 's' : ''}</span>
          </>
        ) : (
          <>
            {(system.steps || []).slice(0, 5).map((step, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="w-2 h-px bg-[#2A2F38]" />}
                <div className="w-1.5 h-1.5 rounded-full bg-[#00E5C020] border border-[#00E5C040]" />
              </React.Fragment>
            ))}
            <span className="text-[10px] text-[#3A4150] ml-1">{(system.steps || []).length} steps</span>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-[rgba(255,255,255,0.08)]">
        <div className="flex items-center gap-2 text-[10px] text-[#5C6778]">
          {latestRun ? (
            <>
              {latestRun.status === 'completed' && <CheckCircle size={10} className="text-[#00E5C0]" />}
              {latestRun.status === 'failed' && <XCircle size={10} className="text-[#EF4444]" />}
              {latestRun.status === 'running' && <Pulse size={10} className="text-[#3B82F6]" />}
              <span>{timeAgo(latestRun.created_at)}</span>
            </>
          ) : (
            <span>No runs yet</span>
          )}
          {system.run_count > 0 && <span className="text-[#3A4150]">{system.run_count} runs</span>}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => onToggle(system.system_id)}
            data-testid={`toggle-${system.system_id}`}
            className="p-1.5 rounded-md hover:bg-[rgba(255,255,255,0.05)] text-[#7D8896] hover:text-[#B0B8C4] transition-colors"
            title={isActive ? 'Pause' : 'Activate'}
          >
            {isActive ? <Pause size={14} /> : <Play size={14} />}
          </button>
          <button
            onClick={() => onDelete(system.system_id)}
            data-testid={`delete-${system.system_id}`}
            className="p-1.5 rounded-md hover:bg-[rgba(239,68,68,0.08)] text-[#5C6778] hover:text-[#EF4444] transition-colors"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Worker type icons ── */
const WORKER_TYPE_ICON = {
  event: Lightning,
  chat: ChatText,
  voice: Microphone,
  file: FileText,
  operator: Globe,
  scheduled: Clock,
};

/* ── Step Indicator ── */
const ModalStepBar = ({ current, total }) => (
  <div className="flex items-center gap-1.5" data-testid="modal-step-bar">
    {Array.from({ length: total }, (_, i) => (
      <div key={i} className="h-[3px] rounded-full transition-all duration-300"
        style={{ width: i + 1 === current ? 28 : 12, backgroundColor: i + 1 <= current ? '#00E5C0' : 'rgba(255,255,255,0.08)' }} />
    ))}
  </div>
);

/* ── Create Flow Modal — 4-Step Orchestration ── */
function CreateSystemModal({ onClose, onCreated }) {
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;

  const [step, setStep] = useState(1);
  const [systemName, setSystemName] = useState('');
  const [triggerType, setTriggerType] = useState('manual');
  const [triggerConfig, setTriggerConfig] = useState({});
  const [creating, setCreating] = useState(false);

  // Orchestration state
  const [workers, setWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(true);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState(new Set());
  const [supervisorInstructions, setSupervisorInstructions] = useState('');

  // Fetch org workers
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const res = await fetchWithOrg(`${BACKEND_URL}/api/workers?status=active`, {}, orgId);
        if (res.ok) {
          const data = await res.json();
          setWorkers(data.workers || []);
        }
      } catch {}
      finally { setLoadingWorkers(false); }
    })();
  }, [orgId]);

  const toggleWorker = (workerId) => {
    setSelectedWorkerIds(prev => {
      const next = new Set(prev);
      if (next.has(workerId)) next.delete(workerId);
      else next.add(workerId);
      return next;
    });
  };

  const selectedWorkers = workers.filter(w => selectedWorkerIds.has(w.id));

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/systems`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: systemName || 'New Orchestration',
          trigger_type: triggerType,
          trigger_config: triggerConfig,
          worker_ids: [...selectedWorkerIds],
          supervisor_instructions: supervisorInstructions,
        }),
      });
      if (res.ok) {
        toast.success('Orchestration activated');        onCreated();
        onClose();
      } else {
        toast.error('Failed to create system');
      }
    } catch {
      toast.error('Failed to create system');
    } finally {
      setCreating(false);
    }
  };

  const STEP_TITLES = {
    1: 'Which workers should be involved?',
    2: 'How should your AI team handle this?',
    3: 'When should it run?',
    4: 'Review and activate',
  };

  const canAdvanceStep1 = selectedWorkerIds.size > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="create-system-modal" onClick={onClose}>
      <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex-1 min-w-0">
            <ModalStepBar current={step} total={4} />
            <h2 className="text-base font-semibold text-[#F0F4F8] mt-3" data-testid="modal-step-title">
              {STEP_TITLES[step]}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#5C6778] hover:text-[#B0B8C4] transition-colors p-1 ml-3" data-testid="close-modal-btn">
            <XCircle size={20} />
          </button>
        </div>

        <div className="p-6 pt-0 overflow-y-auto flex-1">
          {/* ── Step 1: Worker selection ── */}
          {step === 1 && (
            <div data-testid="worker-selection">
              <p className="text-xs text-[#7D8896] mb-4">Select the workers that should participate in this orchestration. They'll be coordinated as a team.</p>

              {loadingWorkers ? (
                <div className="flex items-center justify-center py-12">
                  <ArrowsClockwise size={20} className="text-[#3A4150] animate-spin" />
                </div>
              ) : workers.length === 0 ? (
                <div className="text-center py-10 rounded-xl border border-dashed border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.01)]" data-testid="no-workers-state">
                  <UsersThree size={32} className="mx-auto text-[#2A2F38] mb-3" />
                  <p className="text-sm text-[#7D8896] mb-1">No active workers yet</p>
                  <p className="text-xs text-[#5C6778]">Create workers first, then build systems to orchestrate them.</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="worker-list">
                  {workers.map(w => {
                    const isSelected = selectedWorkerIds.has(w.id);
                    const TypeIcon = WORKER_TYPE_ICON[w.type] || Lightning;
                    return (
                      <button
                        key={w.id}
                        onClick={() => toggleWorker(w.id)}
                        data-testid={`worker-${w.id}`}
                        className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-all text-left ${
                          isSelected
                            ? 'border-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.04)]'
                            : 'border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.04)] hover:border-[rgba(255,255,255,0.1)]'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-[rgba(0,229,192,0.12)]' : 'bg-[rgba(255,255,255,0.04)]'
                        }`}>
                          <TypeIcon size={16} className={isSelected ? 'text-[#00E5C0]' : 'text-[#7D8896]'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className={`text-[13px] font-medium block truncate ${isSelected ? 'text-[#F0F4F8]' : 'text-[#C8D1DB]'}`}>{w.name}</span>
                          <span className="text-[10px] text-[#5C6778] capitalize">{w.type || 'event'} worker</span>
                        </div>
                        <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-all ${
                          isSelected
                            ? 'border-[#00E5C0] bg-[#00E5C0]'
                            : 'border-[rgba(255,255,255,0.12)] bg-transparent'
                        }`}>
                          {isSelected && <CheckCircle size={12} weight="bold" className="text-[#0B0F14]" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {selectedWorkerIds.size > 0 && (
                <div className="mt-3 flex items-center gap-2 text-[11px] text-[#00E5C0]" data-testid="worker-count-badge">
                  <UsersThree size={12} />
                  <span>{selectedWorkerIds.size} worker{selectedWorkerIds.size !== 1 ? 's' : ''} selected</span>
                </div>
              )}

              <div className="flex items-center justify-end mt-5">
                <button
                  onClick={() => setStep(2)}
                  disabled={!canAdvanceStep1}
                  data-testid="continue-to-instructions-btn"
                  className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[rgba(0,229,192,0.1)] text-[#00E5C0] text-sm font-medium hover:bg-[rgba(0,229,192,0.15)] transition-colors border border-[rgba(0,229,192,0.2)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <CaretRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Supervisor instructions ── */}
          {step === 2 && (
            <div data-testid="supervisor-instructions">
              <p className="text-xs text-[#7D8896] mb-4">Tell your AI team how to coordinate. These instructions guide how workers hand off and prioritise tasks.</p>

              <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[rgba(255,255,255,0.04)]">
                  <NotePencil size={14} className="text-[#7D8896]" />
                  <span className="text-[11px] text-[#7D8896] font-medium uppercase tracking-wide">Supervisor instructions</span>
                </div>
                <textarea
                  value={supervisorInstructions}
                  onChange={e => setSupervisorInstructions(e.target.value)}
                  data-testid="supervisor-instructions-input"
                  rows={5}
                  className="w-full bg-transparent px-4 py-3 text-[13px] text-[#F0F4F8] placeholder:text-[#3A4150] outline-none resize-none leading-relaxed"
                  placeholder={"When a new enquiry comes in, respond immediately.\nIf customer is ready, book the job.\nIf no response, follow up after 1 hour."}
                />
              </div>

              {/* Worker summary hint */}
              {selectedWorkers.length > 0 && (
                <div className="mt-4 rounded-lg border border-[rgba(255,255,255,0.04)] bg-[rgba(255,255,255,0.015)] px-3.5 py-2.5" data-testid="worker-hint">
                  <p className="text-[10px] text-[#5C6778] mb-1.5">Workers in this orchestration:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedWorkers.map(w => (
                      <span key={w.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[rgba(0,229,192,0.05)] border border-[rgba(0,229,192,0.1)] text-[10px] text-[#B0B8C4]">
                        {w.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 mt-5">
                <button onClick={() => setStep(1)} className="px-4 py-2.5 rounded-lg text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors" data-testid="back-to-workers-btn">
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  data-testid="continue-to-trigger-btn"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[rgba(0,229,192,0.1)] text-[#00E5C0] text-sm font-medium hover:bg-[rgba(0,229,192,0.15)] transition-colors border border-[rgba(0,229,192,0.2)]"
                >
                  Continue <CaretRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Trigger selection ── */}
          {step === 3 && (
            <div data-testid="trigger-selection">
              <p className="text-xs text-[#7D8896] mb-4">Choose what starts this orchestration.</p>
              <TriggerSelector
                selected={triggerType}
                config={triggerConfig}
                onSelect={setTriggerType}
                onConfigChange={setTriggerConfig}
              />
              <div className="flex items-center gap-3 mt-6">
                <button onClick={() => setStep(2)} className="px-4 py-2.5 rounded-lg text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors" data-testid="back-to-instructions-btn">
                  Back
                </button>
                <button
                  onClick={() => setStep(4)}
                  data-testid="continue-to-review-btn"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[rgba(0,229,192,0.1)] text-[#00E5C0] text-sm font-medium hover:bg-[rgba(0,229,192,0.15)] transition-colors border border-[rgba(0,229,192,0.2)]"
                >
                  Continue <CaretRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4: Review & Activate ── */}
          {step === 4 && (
            <div data-testid="review-flow">
              {/* System name */}
              <input
                type="text"
                value={systemName}
                onChange={e => setSystemName(e.target.value)}
                data-testid="system-name-input"
                className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] rounded-lg px-4 py-2.5 text-[13px] text-[#F0F4F8] mb-5 focus:outline-none focus:border-[rgba(0,229,192,0.3)]"
                placeholder="Orchestration name"
              />

              {/* Workers summary */}
              {selectedWorkers.length > 0 && (
                <div className="mb-4" data-testid="review-workers">
                  <div className="flex items-center gap-2 mb-2.5">
                    <UsersThree size={14} className="text-[#00E5C0]" />
                    <span className="text-[11px] text-[#00E5C0] font-semibold uppercase tracking-wide">AI Team</span>
                  </div>
                  <div className="rounded-xl border border-[rgba(0,229,192,0.1)] bg-[rgba(0,229,192,0.02)] p-4">
                    <p className="text-[12px] text-[#B0B8C4] mb-3">This system will:</p>
                    <div className="space-y-2">
                      {selectedWorkers.map((w) => {
                        const TypeIcon = WORKER_TYPE_ICON[w.type] || Lightning;
                        return (
                          <div key={w.id} className="flex items-center gap-2.5" data-testid={`review-worker-${w.id}`}>
                            <div className="w-1.5 h-1.5 rounded-full bg-[#00E5C0]" />
                            <TypeIcon size={13} className="text-[#7D8896]" />
                            <span className="text-[12px] text-[#F0F4F8]">
                              Use <span className="font-medium text-[#00E5C0]">{w.name}</span>
                              <span className="text-[#5C6778] ml-1">({w.type || 'event'} worker)</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Supervisor instructions summary */}
              {supervisorInstructions.trim() && (
                <div className="mb-4" data-testid="review-instructions">
                  <div className="flex items-center gap-2 mb-2.5">
                    <TextAlignLeft size={14} className="text-[#7D8896]" />
                    <span className="text-[11px] text-[#7D8896] font-semibold uppercase tracking-wide">Supervisor Instructions</span>
                  </div>
                  <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-4">
                    <p className="text-[12px] text-[#B0B8C4] whitespace-pre-wrap leading-relaxed">{supervisorInstructions}</p>
                  </div>
                </div>
              )}

              {/* Trigger summary */}
              <div className="flex items-center gap-2 text-[11px] text-[#7D8896] mb-5" data-testid="review-trigger">
                {(() => { const I = TRIGGER_ICON[triggerType] || HandTap; return <I size={12} />; })()}
                <span>
                  {triggerType === 'email' ? 'Runs on new email'
                    : triggerType === 'webhook' ? 'Runs on form submission'
                    : triggerType === 'sms' ? 'Runs on message'
                    : triggerType === 'schedule'
                      ? `Runs ${triggerConfig.frequency || 'daily'}${triggerConfig.time ? ` at ${triggerConfig.time}` : ''}`
                    : 'Runs manually'}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button onClick={() => setStep(3)} className="px-4 py-2.5 rounded-lg text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors" data-testid="back-to-trigger-btn">
                  Back
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid="activate-system-btn"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#00E5C0] text-[#12161C] text-sm font-semibold hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
                >
                  {creating ? <ArrowsClockwise size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
                  {creating ? 'Activating...' : 'Activate Orchestration'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════ */
export default function SystemsPage() {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;

  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchSystems = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/systems`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSystems(data.systems || []);
      }
    } catch {}
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { fetchSystems(); }, [fetchSystems]);

  const handleToggle = async (systemId) => {
    const res = await fetch(`${BACKEND_URL}/api/systems/${systemId}/toggle`, { method: 'POST', credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      toast.success(data.status === 'active' ? 'Orchestration activated' : 'Orchestration paused');
      fetchSystems();
    }
  };

  const handleDelete = async (systemId) => {
    if (!window.confirm('Remove this orchestration?')) return;
    const res = await fetch(`${BACKEND_URL}/api/systems/${systemId}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) { toast.success('System removed'); fetchSystems(); }
  };

  return (
    <div className="max-w-[960px] mx-auto" data-testid="systems-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/workers')} data-testid="back-to-workers-btn"
            className="flex items-center gap-1 text-[#7D8896] hover:text-[#B0B8C4] text-xs transition-colors">
            <ArrowLeft size={14} /> Workers
          </button>
          <div className="w-px h-4 bg-[rgba(255,255,255,0.06)]" />
          <h1 className="text-lg font-semibold text-[#F0F4F8]" data-testid="page-title">Orchestrations</h1>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="create-system-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#00E5C0] text-[#12161C] text-sm font-semibold hover:bg-[#00CCB0] transition-colors"
        >
          <Plus size={14} weight="bold" /> Create Orchestration
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20" data-testid="loading-state">
          <ArrowsClockwise size={20} className="text-[#3A4150] animate-spin" />
        </div>
      ) : systems.length === 0 ? (
        <div className="text-center py-20" data-testid="empty-state">
          <UsersThree size={40} className="mx-auto text-[#2A2F38] mb-4" />
          <p className="text-sm text-[#7D8896] mb-2">No orchestrations yet</p>
          <p className="text-xs text-[#5C6778] mb-6">Orchestrations coordinate your AI workers as a team to handle business processes end-to-end</p>
          <button onClick={() => setShowCreate(true)} data-testid="create-first-system-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#00E5C0] text-[#12161C] text-sm font-semibold hover:bg-[#00CCB0] transition-colors">
            <Plus size={14} weight="bold" /> Build your first orchestration
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="systems-grid">
          {systems.map(sys => (
            <SystemCard
              key={sys.system_id}
              system={sys}
              onToggle={handleToggle}
              onDelete={handleDelete}
              onClick={() => navigate(`/systems/${sys.system_id}`)}
            />
          ))}
        </div>
      )}

      {/* Improvement feed */}
      <div className="mt-10">
        <ImprovementFeed limit={5} />
      </div>

      {/* Create modal */}
      {showCreate && (
        <CreateSystemModal
          onClose={() => setShowCreate(false)}
          onCreated={fetchSystems}
        />
      )}
    </div>
  );
}
